<template>
  <no-auth v-if="!hasAuthorize.cycleSelection"></no-auth>
  <div v-else class="container-fluid o-card">
    <h4 class="u-margin-bottom-small">
      {{ $t("cycles.selection") }}
    </h4>
    <hr />
    <div class="row">
      <div class="es-xs-12 es-lg-3 o-card o-card__padding-small">
        <div>
          <SeriesSearch
            v-model="series"
            class="u-display-flex u-align-items-center u-justify-content-space-between u-margin-xxsmall u-flex-1-with-child c-form-control__variant--xs"
            :label="$t('cyclesForm.series')"
            :validActivated="validActivated"
          />
        </div>
        <div>
          <RefrigerantBySeriesSearch
            :disabled="!series"
            class="u-display-flex u-align-items-center u-justify-content-space-between u-margin-xxsmall u-flex-1-with-child c-form-control__variant--xs"
            :validActivated="validActivated"
            :label="$t('cyclesForm.refrigerant')"
            v-model="refrigerant"
            :query="{ seriesId: series?.id }"
          />
        </div>
        <BaseInput
          size="xs"
          type="number"
          class="u-display-flex u-align-items-center u-justify-content-space-between u-margin-xxsmall u-flex-1-with-child"
          :placeholder="
            $t('common.enterPlaceholder', {
              label: $t('cyclesForm.condenserAirInletTemp'),
            })
          "
          block
          suffix="°C"
          v-model="form.condenserAirInletTemp"
          :label="$t('cyclesForm.condenserAirInletTemp')"
          :mandotary="validActivated"
        />
        <BaseInput
          size="xs"
          type="number"
          class="u-display-flex u-align-items-center u-justify-content-space-between u-margin-xxsmall u-flex-1-with-child"
          block
          suffix="°C"
          v-model="form.evaporatorAirInletTemp"
          :label="$t('cyclesForm.evaporatorAirInletTemp')"
          :mandotary="validActivated"
        />
        <BaseInput
          size="xs"
          type="number"
          class="u-display-flex u-align-items-center u-justify-content-space-between u-margin-xxsmall u-flex-1-with-child"
          block
          suffix="%"
          v-model="form.evaporatorAirInletHumidity"
          :label="$t('cyclesForm.evaporatorAirInletHumidity')"
          :mandotary="validActivated"
        />
        <BaseInput
          size="xs"
          type="number"
          class="u-display-flex u-align-items-center u-justify-content-space-between u-margin-xxsmall u-flex-1-with-child"
          block
          suffix="kW"
          v-model="form.coolingCapacity"
          :label="$t('cyclesForm.coolingCapacity')"
          :mandotary="validActivated"
        />
        <BaseInput
          size="xs"
          type="number"
          :min="0"
          :max="100"
          class="u-display-flex u-align-items-center u-justify-content-space-between u-margin-xxsmall u-flex-1-with-child"
          block
          v-model="form.tolerance"
          :label="$t('cyclesForm.tolerance')"
          :mandotary="validActivated"
        />
        <Button
          variant="primary"
          class="u-margin-top-small"
          block
          @click="calculate"
          >{{ $t("common.calculate") }}</Button
        >
      </div>
      <div
        class="es-xs-12 es-lg-5 o-card o-card__padding-small o-card__height--400"
      >
        <loading v-if="selectionStatus === 'loading'"></loading>
        <not-found
          v-else-if="
            selectionStatus === 'error' ||
            (selectionStatus === 'success' && selections.length === 0)
          "
        ></not-found>
        <ul v-else class="c-list">
          <li
            class="c-list__item"
            :class="{
              'c-list__item--active': selectedProject?.id === selection.id,
            }"
            v-for="selection in selections"
            :key="selection.id"
            @click="selectedProject = selection"
          >
            <div class="u-display-flex u-align-items-center">
              <Checkbox
                :opposite="selectedProject?.id === selection.id"
                :value="selectedProject?.id === selection.id"
                readonly
              ></Checkbox>
              <span>{{ selection.projectInfo.model }}</span>
            </div>
            <div>
              {{ $t("cyclesForm.coolingCapacity") }}:
              <span>{{ selection.coolingCapacity }}</span>
            </div>
          </li>
        </ul>
      </div>
      <div
        class="es-xs-12 es-lg-4 o-card o-card__padding-small"
        v-if="selectedProject"
      >
        <cooling-capacity
          ref="coolingCapacity"
          :refrigerants="selectedProject.refrigerantInfo.id"
          @request="CalculateCycle"
          :model="selectedProject.projectInfo.model"
          @export="exportCoolingCapacityExcel"
          prevent-download
        ></cooling-capacity>

        <FileListInSelectBox
          :path="startPath"
          :project="selectedProject.projectInfo"
          :label="$t('common.exportPdf')"
        ></FileListInSelectBox>
        <div
          class="u-margin-top-small u-display-flex u-align-items-center u-justify-content-flex-end"
        >
          <router-link
            target="_blank"
            :to="{
              name: 'project-detail',
              params: { projectId: selectedProject.projectInfo.id },
            }"
          >
            <Button variant="light-primary">
              {{ $t("common.goToProject") }}
              <i class="icofont-external-link"></i>
            </Button>
          </router-link>
        </div>
      </div>
    </div>
    <div v-if="coolingCapactyHtml.html" class="s-cooling-capacity-table">
      <div v-html="coolingCapactyHtml.html"></div>
      <div
        class="u-display-flex u-align-items-center u-justify-content-flex-end"
      >
        <Button size="lg" @click="exportExcel" variant="light-success" block>
          <i class="icofont-download"></i>
          {{ $t("common.export") }}</Button
        >
      </div>
    </div>
  </div>
</template>

<script>
import SeriesSearch from "@/components/Searches/SeriesSearch.vue";
import BaseInput from "@/components/Base/Inputs/BaseInput.vue";
import RefrigerantBySeriesSearch from "../Searches/RefrigerantBySeriesSearch.vue";
import Button from "@/components/Base/Button.vue";
import Checkbox from "@/components/Base/Inputs/Checkbox.vue";
import Requests from "@/services/Requests";
import CoolingCapacity from "@/components/Cycles/CycleForm/CoolingCapacity.vue";
import FileListInSelectBox from "@/components/FileManager/FileListInSelectBox.vue";
import NotFound from "@/components/Base/NotFound.vue";
import authorize from "../Cycles/authorize";
export default {
  mixins: [authorize],
  components: {
    SeriesSearch,
    BaseInput,
    RefrigerantBySeriesSearch,
    Button,
    Checkbox,
    CoolingCapacity,
    FileListInSelectBox,
    NotFound,
  },
  data() {
    return {
      series: null,
      refrigerant: null,
      validActivated: false,
      selectedProject: null,
      form: {
        condenserAirInletTemp: 50,
        evaporatorAirInletTemp: 27,
        evaporatorAirInletHumidity: 50,
        coolingCapacity: 8,
        tolerance: 20,
      },
      selections: [],
      selectionStatus: "start",
      cycle: null,
      coolingCapactyHtml: {
        html: null,
        blob: null,
      },
    };
  },
  computed: {
    startPath() {
      if (!this.selectedProject) return "";
      const project = this.selectedProject.projectInfo;
      const guid = project.guid;
      return `Project/${guid}`;
    },
  },
  methods: {
    validation() {
      this.validActivated = true;
      if (!this.series)
        return this.VALIDATION_ERROR_MESSAGE(
          this.$t("message.required.description", {
            field: this.$t("cyclesForm.series"),
          })
        );
      if (!this.refrigerant)
        return this.VALIDATION_ERROR_MESSAGE(
          this.$t("message.required.description", {
            field: this.$t("cyclesForm.refrigerant"),
          })
        );
      if (!this.form.condenserAirInletTemp)
        return this.VALIDATION_ERROR_MESSAGE(
          this.$t("message.required.description", {
            field: this.$t("cyclesForm.condenserAirInletTemp"),
          })
        );
      if (!this.form.evaporatorAirInletTemp)
        return this.VALIDATION_ERROR_MESSAGE(
          this.$t("message.required.description", {
            field: this.$t("cyclesForm.evaporatorAirInletTemp"),
          })
        );
      if (!this.form.evaporatorAirInletHumidity)
        return this.VALIDATION_ERROR_MESSAGE(
          this.$t("message.required.description", {
            field: this.$t("cyclesForm.evaporatorAirInletHumidity"),
          })
        );
      if (!this.form.coolingCapacity)
        return this.VALIDATION_ERROR_MESSAGE(
          this.$t("message.required.description", {
            field: this.$t("cyclesForm.coolingCapacity"),
          })
        );
      if (
        !this.form.tolerance ||
        this.form.tolerance < 0 ||
        this.form.tolerance > 100
      )
        return this.VALIDATION_ERROR_MESSAGE(
          this.$t("message.required.description", {
            field: this.$t("cyclesForm.tolerance"),
          })
        );
      return "OK";
    },
    async calculate(e, loading) {
      const valid = this.validation();
      if (valid !== "OK") return loading(false);
      this.selectionStatus = "loading";
      loading(true);
      const response = await Requests.Cycle.CalculateCycleSelection({
        ...this.form,
        seriesId: this.series.id,
        refrigerantId: this.refrigerant.id,
      });
      if (response.message === "OK") {
        this.selections = response.data;
        this.selectionStatus = "success";
      } else {
        this.selectionStatus = "error";
      }
      loading(false);
    },
    VALIDATION_ERROR_MESSAGE(message) {
      this.$store.dispatch("SET_ACTION_MODAL", {
        isLoading: false,
        status: true,
        message: message,
        variant: "danger",
        duration: 2500,
      });
      return "ERROR";
    },
    setForm(cycleItem = {}) {
      return {
        projectId: this.cycle.projectInfo.id,
        refrigerantId: this.cycle.refrigerantInfo.id,
        superheat: this.cycle.superheat,
        subcooling: this.cycle.subcooling,
        condensingTemp: this.cycle.cycleCalculateValueInfo.condensingTemp,
        condenserFanFlow: this.cycle.condenserFanFlow,
        condenserAirInletTemp: this.cycle.condenserAirInletTemp,
        condenserAirInletHumidity: this.cycle.condenserAirInletHumidity,
        evaporatorAirInletTemp: this.cycle.evaporatorAirInletTemp,
        evaporatorAirInletHumidity: this.cycle.evaporatorAirInletHumidity,
        evaporatorFanFlow: this.cycle.evaporatorFanFlow,
        evapBatteryId: this.cycle.evaporatorBatteryInfo.id,
        condBatteryId: this.cycle.condenserBatteryInfo.id,
        evapQuantity: this.cycle.cycleCalculateValueInfo.evapQuantity,
        condQuantity: this.cycle.cycleCalculateValueInfo.condQuantity,
        compressorBrandId:
          this.cycle.cycleCalculateValueInfo.compressorBrandInfo.id,
        compressorRefrigerantId:
          this.cycle.cycleCalculateValueInfo.compressorBrandRefrigerantInfo.id,
        compRefrigetantModelId:
          this.cycle.cycleCalculateValueInfo.compressorRefrigerantModelInfo.id,
        compressorQuantity:
          this.cycle.cycleCalculateValueInfo.compressorQuantity,
        constantAirFlowRate:
          this.cycle.cycleCalculateValueInfo.constantAirFlowRate,
        variableAirFlowRate:
          this.cycle.cycleCalculateValueInfo.variableAirFlowRate,
        evapAbsoluteFlowDifference: 1,
        condAbsoluteFlowDifference: 1,
        ...cycleItem,
      };
    },
    async getCycle() {
      const cycleId = this.selectedProject.id;
      const response = await Requests.Cycle.GetById({
        params: { id: cycleId },
      });
      if (response.message === "OK") {
        this.cycle = response.data;
      }
      return response;
    },
    async CalculateCycle(results, callback, withHavaDebili) {
      this.$refs.coolingCapacity.showLoadingModal = true;
      const cycle = await this.getCycle();
      if (cycle.message !== "OK")
        return this.VALIDATION_ERROR_MESSAGE(
          this.$t(`message.${cycle.message}.description`)
        );
      const response = await Requests.Cycle.CalculateMultipleCycle({
        ...this.setForm(),
        multipleCalculateCycleRequest: results,
        withHavaDebili,
      });
      if (response.message === "OK") {
        callback(response.data, "success", withHavaDebili);
      } else {
        callback(response, "error", withHavaDebili);
      }
    },
    exportCoolingCapacityExcel(result) {
      this.coolingCapactyHtml = result;
    },
    exportExcel() {
      console.log(this.coolingCapactyHtml, " reeeess");
      const url = window.URL.createObjectURL(this.coolingCapactyHtml.blob);
      const a = document.createElement("a");
      a.href = url;
      a.download =
        this.cycle.serialNumber +
        "_" +
        this.cycle.projectInfo.model +
        "_cooling_capacity.xlsx";
      a.click();
      window.URL.revokeObjectURL(url);
    },
  },
};
</script>

<style></style>
